import { StemType } from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import React, { FC, forwardRef } from 'react'
import { GRAY_COLORS, LOOP_COLORS, styled } from '../../lib/stitches'
import { useTreeState } from '../../stores/Root'
import {
  Column,
  Row,
  StyledScrollable,
  TooltipIconButton,
  VerticalCenteredRow
} from '../core/Components'
import { ZoomIn, ZoomOut } from '../core/icons'
import { FIRST_COLUMN_WIDTH, RECORD_MARKER_HEIGHT } from '../studio/Studio'
import CompositionViewBackground from './CompositionViewBackground'
import LoopBarMarker from './controls/LoopBarMarker'
import RecordMarker from './controls/RecordMarker'
import { StemAndSampleDropzone } from './Dropzone'
import useTrackTable from './hooks/useTrackTable'
import AudioStemTrack from './tracks/AudioStemTrack'
import InstrumentTrackHeader from './tracks/InstrumentTrackHeader'
import InstrumentTrackStem from './tracks/InstrumentTrackStem'

const ZoomInIcon = styled(ZoomIn, { width: '17px', height: '17px' })
const ZoomOutIcon = styled(ZoomOut, { width: '17px', height: '17px' })

const PlayheadContainer = styled(Column, {
  alignItems: 'stretch',
  backgroundColor: '$gray01',
  zIndex: '6'
})

interface ZoomInZoomOutProps {
  height: string
  zoomIn: () => void
  zoomOut: () => void
}
const ZoomInZoomOut: FC<ZoomInZoomOutProps> = ({ height, zoomIn, zoomOut }) => (
  <VerticalCenteredRow
    css={{
      height: height,
      justifyContent: 'end',
      color: 'grey'
    }}
  >
    <TooltipIconButton onClick={zoomOut} tooltipText={'Zoom Out'}>
      <ZoomOutIcon />
    </TooltipIconButton>
    <TooltipIconButton onClick={zoomIn} tooltipText={'Zoom In'} css={{ marginRight: '8px' }}>
      <ZoomInIcon />
    </TooltipIconButton>
  </VerticalCenteredRow>
)

interface TrackStemsMeatProps {
  trackHeight: string
  instrumentLoopWidthPX: number
}
const TrackStemsMeat: FC<TrackStemsMeatProps> = observer(
  ({ trackHeight, instrumentLoopWidthPX }) => {
    const { studio } = useTreeState()

    return (
      <Column>
        {studio.song.stems.map((stem, index) =>
          stem.type() === StemType.AUDIO ? (
            <AudioStemTrack
              trackHeight={trackHeight}
              stemID={stem.uniqueId}
              instrumentLoopWidthPX={instrumentLoopWidthPX}
              key={stem.uniqueId}
            />
          ) : (
            <InstrumentTrackStem
              loopColor={
                LOOP_COLORS[stem.colorIndex % LOOP_COLORS.length] ||
                LOOP_COLORS[index % LOOP_COLORS.length]
              }
              height={trackHeight}
              stemID={stem.uniqueId}
              trackHeight={trackHeight}
              instrumentLoopWidthPX={instrumentLoopWidthPX}
              key={stem.uniqueId}
            />
          )
        )}
      </Column>
    )
  }
)

interface TrackStemsContainerProps {
  onMouseDown: () => void
  onScroll: (e) => void
  children: JSX.Element | JSX.Element[]
}
const TrackStemsContainer = forwardRef(
  (
    { onMouseDown, onScroll, children }: TrackStemsContainerProps,
    ref: React.RefObject<HTMLDivElement>
  ) => (
    <StyledScrollable
      ref={ref}
      id="track-stems"
      className="scrollsync unselectable"
      css={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        position: 'relative',
        overflowY: 'visible',
        overflowX: 'scroll',
        backgroundColor: 'white'
      }}
      onMouseDown={onMouseDown}
      onScroll={onScroll}
    >
      {children}
    </StyledScrollable>
  )
)

interface TrackHeadersMeatProps {
  trackHeight: string
  openSampleDrawer: () => void
}
const TrackHeadersMeat: FC<TrackHeadersMeatProps> = observer(
  ({ trackHeight, openSampleDrawer }) => {
    const { studio } = useTreeState()

    return (
      <Column>
        {studio.song.stems.map((stem, stemIndex) => (
          <InstrumentTrackHeader
            height={trackHeight}
            stemID={stem.uniqueId}
            openSampleDrawer={openSampleDrawer}
            key={stem.uniqueId}
          />
        ))}
      </Column>
    )
  }
)

interface TrackHeadersContainerProps {
  width: string
  trackStems: HTMLDivElement
  children: JSX.Element | JSX.Element[]
}
const TrackHeadersContainer: FC<TrackHeadersContainerProps> = ({ width, trackStems, children }) => (
  <Column
    id="track-headers"
    className="hidden-scrollbar scrollsync position-sticky"
    style={{
      paddingBottom: trackStems
        ? String(trackStems.offsetHeight - trackStems.clientHeight) + 'px'
        : '0px',
      minWidth: width,
      maxWidth: width,
      maxHeight: 'none',
      borderRight: '1px solid var(--studio-medium)',
      backgroundColor: GRAY_COLORS.gray01,
      overflowY: 'auto',
      overflowX: 'visible'
    }}
  >
    {children}
  </Column>
)

const TrackTableContainer = styled(Row, {
  width: '100%',
  minWidth: '100%',
  backgroundColor: 'var(--studio-very-light)',
  variants: {
    isOver: {
      true: {
        backgroundColor: 'var(--studio-light)'
      }
    }
  }
})

const TrackTable: FC<any> = ({
  height,
  openSampleDrawer,
  instrumentLoopWidthPX,
  zoomIn,
  zoomOut
}) => {
  const { trackStems, onScroll, setPlayheadCoords, onClickTrackStemsContainer } = useTrackTable()
  const { studio } = useTreeState()

  const trackHeight = '64px'
  const song = studio.song

  return (
    <TrackTableContainer css={{ height: height }}>
      {/* Instrument Track Headers */}
      <TrackHeadersContainer width={FIRST_COLUMN_WIDTH} trackStems={trackStems.current}>
        <Row
          className="sticky-top"
          css={{
            borderBottom: '1px solid var(--studio-medium)',
            backgroundColor: GRAY_COLORS.gray01,
            height: '30px'
          }}
          verticalCenter
          justifyContentEnd
        >
          <ZoomInZoomOut height={RECORD_MARKER_HEIGHT} zoomIn={zoomIn} zoomOut={zoomOut} />
        </Row>
        <TrackHeadersMeat trackHeight={trackHeight} openSampleDrawer={openSampleDrawer} />
        <StemAndSampleDropzone height={trackHeight} />
      </TrackHeadersContainer>

      {/* Instrument Track Stems */}
      <TrackStemsContainer
        ref={trackStems}
        onMouseDown={onClickTrackStemsContainer}
        onScroll={onScroll}
      >
        <PlayheadContainer
          className="sticky-top"
          css={{
            height: RECORD_MARKER_HEIGHT
          }}
        >
          <LoopBarMarker
            height={`calc(${RECORD_MARKER_HEIGHT} / 2)`}
            instrumentLoopWidthPX={instrumentLoopWidthPX}
          />
          <RecordMarker
            height={`calc(${RECORD_MARKER_HEIGHT} / 2)`}
            setPlayheadCoords={setPlayheadCoords}
            instrumentLoopWidthPX={instrumentLoopWidthPX}
          />
        </PlayheadContainer>
        <TrackStemsMeat trackHeight={trackHeight} instrumentLoopWidthPX={instrumentLoopWidthPX} />

        {/* The empty instrument track stem for the drag and drop of new samples */}
        <CompositionViewBackground
          instrumentLoopWidthPX={instrumentLoopWidthPX}
          height={trackHeight}
          style={{ zIndex: '2' }}
          scrollable
        />

        {/* The "grid" background that runs behind the entire composition view */}
        <CompositionViewBackground
          instrumentLoopWidthPX={instrumentLoopWidthPX}
          height={`calc(${height} - ${RECORD_MARKER_HEIGHT} - (${song.stems.length} * ${trackHeight}))`}
        />
      </TrackStemsContainer>
    </TrackTableContainer>
  )
}

export default observer(TrackTable)
